import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';
import { grillaProductosItemsDoc } from '../shared/grillaProductosItemsDoc';
import { grillaProductosDetalleSections } from '../shared/grillaProductosDetalleSections';

/** Documentación detallada (plantilla 12 apartados) — Inicio y acceso. */
const baseGeneralSections = [
  {
    id: "general-login",
    name: "Inicio de sesión",
    type: "screen",
    route: "/login",
    locationPath: "Acceso → Inicio de sesión",
    generalDescription: "Pantalla pública de autenticación. Valida correo y contraseña contra el API, obtiene token Sanctum y carga el perfil del usuario con módulos habilitados, rol (administrador, limitado, contador) y alcance de almacenes, puntos de venta o sedes de compra. Sin sesión válida no puede acceder a rutas internas; un 401 cierra la sesión y redirige aquí.",
    prerequisites: [
      "Cuenta creada por el administrador en Administrador → Usuarios.",
      "Navegador con cookies/localStorage habilitado."
    ],
    interfaceElements: [
      {
        name: "Correo electrónico",
        controlType: "Texto",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificador de usuario registrado.",
        behavior: "Revise el comportamiento al interactuar en pantalla.",
        example: "operador@empresa.com"
      },
      {
        name: "Contraseña",
        controlType: "Contraseña",
        required: true,
        editable: true,
        automatic: false,
        description: "Clave asignada por el administrador.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Mantenerme conectado",
        controlType: "Casilla",
        required: false,
        editable: true,
        automatic: false,
        description: "Opción visual en el formulario; la sesión depende del token almacenado en el navegador.",
        behavior: "No sustituye políticas de seguridad del administrador; si cierra el navegador puede volver a pedir credenciales según configuración."
      },
      {
        name: "Ingresar",
        controlType: "Botón",
        required: true,
        editable: true,
        automatic: false,
        description: "Envía correo y contraseña al API de login.",
        behavior: "Mientras procesa muestra «Ingresando…». Tras éxito redirige al Dashboard o, si es contador, a Compras diferenciadas."
      },
      {
        name: "¿Olvidaste tu contraseña?",
        controlType: "Enlace",
        required: false,
        editable: false,
        automatic: false,
        description: "Enlace informativo en la parte inferior del formulario.",
        behavior: "Verificar en pantalla si el cliente tiene recuperación activa; de no haberla, solicite reseteo al administrador en Administrador → Usuarios."
      }
    ],
    procedure: [
      "Abra la URL de la aplicación en el navegador; si no hay sesión activa, verá la pantalla «Iniciar Sesión» con el título Contifine Logistic.",
      "En el campo Correo Electrónico escriba su usuario corporativo (formato correo).",
      "En Contraseña ingrese la clave asignada; los caracteres se ocultan.",
      "Opcional: marque Mantenerme conectado si su empresa lo permite.",
      "Pulse el botón morado Ingresar y espere; no cierre la ventana mientras dice «Ingresando…».",
      "Si las credenciales son correctas, el sistema le llevará al Dashboard o a Contabilidad según su rol.",
      "Si aparece un recuadro rojo con mensaje de error, corrija correo o contraseña o contacte al administrador."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Inicio de sesión"
      },
      steps: [
        "Abra la URL de la aplicación; si no hay sesión, verá el formulario de login.",
        "Ingrese correo y contraseña.",
        "Pulse Iniciar sesión y espere la validación.",
        "Tras el éxito, use el menú lateral para ir a su módulo de trabajo."
      ]
    },
    validations: [
      "Correo y contraseña obligatorios.",
      "Credenciales incorrectas muestran mensaje sin revelar cuál campo falló."
    ],
    expectedResult: [
      "Sesión activa con menú acorde a módulos y permisos.",
      "Token almacenado para llamadas al API."
    ],
    commonErrors: [
      {
        error: "Credenciales inválidas",
        cause: "Correo o contraseña incorrectos.",
        solution: "Verifique mayúsculas; pida reseteo al administrador."
      },
      {
        error: "Pantalla en blanco tras login",
        cause: "Error de red o API caído.",
        solution: "Confirme REACT_APP_API_URL y que el backend responda."
      }
    ],
    relatedFunctions: [
      "general-dashboard"
    ],
    observations: [
      "Los módulos visibles dependen de Administrador → Habilitación de módulos."
    ],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: "Todos los usuarios antes de operar el sistema."
  },

  {
    id: "general-dashboard",
    name: "Dashboard principal",
    type: "screen",
    route: "/dashboard",
    locationPath: "Inicio → Dashboard",
    generalDescription: "Panel de bienvenida tras autenticarse. Muestra accesos rápidos y, según módulos activos, secciones resumidas (por ejemplo ventas). Es el punto de partida para navegar al manual de usuario (enlace superior del menú lateral) y a cada módulo operativo.",
    prerequisites: [
      "Sesión iniciada.",
      "El administrador puede ocultar el dashboard a algunos usuarios limitados."
    ],
    interfaceElements: [
      {
        name: "Menú lateral",
        controlType: "Menú",
        required: false,
        editable: true,
        automatic: true,
        description: "Navegación por módulos habilitados.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Manual de usuario",
        controlType: "Enlace",
        required: false,
        editable: true,
        automatic: false,
        description: "Abre la guía en /manual filtrada por su rol y módulos.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tarjetas / resúmenes",
        controlType: "Panel",
        required: false,
        editable: true,
        automatic: true,
        description: "Indicadores según configuración del cliente.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Tras iniciar sesión llegará al dashboard salvo redirección por rol (ej. contador).",
      "Revise indicadores si están disponibles.",
      "Use el menú lateral para ir a Compras, Ventas, Almacenes u otro módulo.",
      "Abra el manual si necesita pasos detallados de una pantalla."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Dashboard principal"
      },
      steps: [
        "Tras iniciar sesión llegará al dashboard salvo redirección por rol (ej. contador).",
        "Revise indicadores si están disponibles.",
        "Use el menú lateral para ir a Compras, Ventas, Almacenes u otro módulo.",
        "Abra el manual si necesita pasos detallados de una pantalla."
      ]
    },
    validations: [
      "Si no ve un módulo, no es error del dashboard: revise habilitación y permisos."
    ],
    expectedResult: [
      "Navegación clara hacia las funciones autorizadas.",
      "Contexto de usuario visible en la barra superior cuando aplique (POS, almacén)."
    ],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "general-login"
    ],
    observations: [
      ROLES.limitado,
      "Contadores pueden entrar directo a Contabilidad según configuración."
    ],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: "Cualquier usuario autenticado con dashboard visible."
  },

  {
    id: "general-manual",
    name: "Manual de usuario (visor)",
    type: "screen",
    route: "/manual",
    locationPath: "Menú lateral (parte superior) → Manual de usuario",
    generalDescription:
      "Guía integrada en la aplicación con el mismo contenido que este documento estructurado: cada función del menú aparece con los 12 apartados (descripción, ubicación, requisitos, interfaz, pasos, ejemplo, validaciones, resultado, errores, relaciones y observaciones). El índice lateral lista solo los módulos que su usuario puede ver según rol y módulos habilitados.",
    prerequisites: [
      "Sesión iniciada (el manual no sustituye el login).",
      "Módulos activos en Administrador → Habilitación de módulos para que aparezcan capítulos completos."
    ],
    interfaceElements: [
      {
        name: "Enlace Manual de usuario",
        controlType: "Enlace",
        required: false,
        editable: true,
        automatic: false,
        description: "Texto con icono 📖 sobre el logo Contifine Logistic en el menú lateral.",
        behavior: "Abre la ruta /manual sin cerrar la sesión."
      },
      {
        name: "Índice de módulos",
        controlType: "Menú lateral",
        required: false,
        editable: true,
        automatic: true,
        description: "Lista General, Gestión, Compras, Ventas, etc., según permisos.",
        behavior: "Al elegir un módulo se despliegan las funciones documentadas; el clic ancla a cada apartado."
      },
      {
        name: "Contenido de la función",
        controlType: "Panel",
        required: false,
        editable: false,
        automatic: true,
        description: "Títulos numerados del 1 al 12 por cada función.",
        behavior: "Incluye placeholders para capturas de pantalla donde el implementador debe insertar imágenes."
      },
      {
        name: "Versión del manual",
        controlType: "Texto",
        required: false,
        editable: false,
        automatic: true,
        description: "Identificador de revisión en el pie o cabecera del visor.",
        behavior: "Sirve para confirmar con soporte si tiene la última documentación desplegada."
      }
    ],
    procedure: [
      "Con sesión activa, localice en el menú lateral el enlace Manual de usuario (arriba del nombre Contifine Logistic).",
      "Pulse el enlace; se abrirá la página del manual en el área principal.",
      "En el índice izquierdo expanda el módulo que necesita (por ejemplo Ventas o Compras).",
      "Seleccione la función concreta (listado, nuevo, reporte) y lea los apartados en orden.",
      "Use la ruta indicada en «Ubicación en el sistema» para abrir la pantalla real en otra pestaña si desea practicar en paralelo.",
      "Si no encuentra un ítem que sí ve en el menú operativo, informe a soporte: puede faltar actualizar el manual o su rol oculta ese capítulo."
    ],
    practicalExample: {
      context: "Operador de compras que registra su primera orden de compra.",
      data: {
        Módulo: "Compras",
        Función: "Orden de compra — nueva"
      },
      steps: [
        "Abra Manual de usuario desde el menú lateral.",
        "Índice → Compras → busque la función de creación de OC.",
        "Siga el procedimiento paso a paso y los campos obligatorios listados en «Descripción de la interfaz».",
        "Vuelva a Compras → Órdenes de compra → Nuevo y complete el formulario siguiendo el ejemplo práctico."
      ]
    },
    validations: [
      "Usuarios limitados solo ven módulos de su alcance; el manual no muestra capítulos de módulos deshabilitados.",
      "El rol contador puede tener índice reducido si solo tiene Contabilidad visible."
    ],
    expectedResult: [
      "Navegación fluida entre funciones sin salir de la aplicación.",
      "Cada función muestra los 12 apartados con lenguaje operativo en español."
    ],
    commonErrors: [
      {
        error: "El manual no lista un módulo que uso a diario",
        cause: "Flag de módulo apagado o usuario limitado sin permiso.",
        solution: "Compare con el menú lateral; solicite habilitación al administrador."
      },
      {
        error: "Texto genérico «Revise el comportamiento en pantalla»",
        cause: "Sección aún no detallada en esa versión del manual.",
        solution: "Reporte la pantalla concreta para ampliar la documentación en la siguiente actualización."
      }
    ],
    relatedFunctions: [
      "general-login",
      "general-dashboard",
      "general-grilla-productos-items",
      "admin-modulos"
    ],
    observations: [
      "La documentación vive en el código del frontend; tras cada despliegue conviene recargar con Ctrl+F5.",
      SHOTS.vistaGeneral
    ],
    screenshots: [SHOTS.vistaGeneral],
    roles: "Cualquier usuario autenticado."
  }
].map((p) => functionDoc(p));

/** Grilla transversal (Compras, Ventas, Almacenes) + capítulos por módulo. */
export const generalSections = [
  ...baseGeneralSections,
  grillaProductosItemsDoc,
  ...grillaProductosDetalleSections,
];
